/* !!! DO NOT CHANGE THIS FILE WITHOUT APPROVAL FROM ALL CLIENT TEAMS !!! */
/* eslint-disable import/no-relative-packages, i18next/no-literal-string */
import { store } from 'App';
import { authlog } from 'AuthLogger';
import { pageDataService } from 'common';
import { ThemeType } from 'common/theme';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import {
  initializeNativeAppState,
  restartPageLoad,
  setAuthResult,
  setCustomFontSize,
  setDialogClosing,
  setWebViewLocale,
  setWebViewTheme,
} from 'store';
import { blurBackground, setCustomScrollBar } from 'store/layout/layoutSlice';
import { navigateWebViewDialogUrl, navigateWebViewPageUrl, refreshData, updateWebViewState } from 'store/webView/webViewActions';

import { Platform } from './generated/clients/TypeScript/MicrosoftConsumerSecurityClient/v1/src/models';

interface INativeHostObject {
  /** Native Api will be store in global window object under nativeAppApi variable */
  nativeAppApi: INativeAppApi;

  /**
   * Native State will be store in global window object under nativeAppState variable
   * Copy will be saved in Redux store. UI will use Redux store data render.
   */
  nativeAppState: INativeAppState;
}

export type DialogSize = 'small' | 'large' | 'full';

export enum AuthResult {
  /**
   * Default value. This means authentication is not yet completed.
   */
  None = 'None',
  /**
   * Authentication is successful
   */
  Success = 'Success',
  /**
   * Authentication is failed
   */
  Failure = 'Failure',
  /**
   * NotSupported is used when the platform authentication is not supported on the device
   * In such case the webview would show a fallback authentication (web login popup)
   */
  NotSupported = 'NotSupported',
}

// MAKE SURE TO SYNC WITH CLIENT TEAMS BEFORE ANY CHANGES!
export interface INativeAppState {
  /**
   * ==========================================
   * Initial contract
   * ==========================================
   */
  /** Version of native APIs and States. Current version: 1.0 */
  version: string;
  /**  "Windows" | "Android" | "IOS" | "MacOS" */
  platform: Platform;
  /** Fallback "en" */
  locale: string;

  /**
   * ==========================================
   * Additional contract
   * ==========================================
   * Note that the following properties are optional and could cause serialization issue when accessing it in webview
   * Reading these properties should be wrapped in try-catch block. Example: getFontScale function.
   */
  /**
   * Font size scale factor.
   * ex) 1 for default size. 0.8 for 80% of default size. 1.2 for 120% of default size
   */
  fontScale?: string;
  /** Whether or not the client app supports a local authentication */
  supportsLocalAuth?: boolean;
  /**
   * Whether or not the client app supports dark theme.
   * If false, WebView will always show Light theme.
   * If true, WebView will check the theme property to determine the theme.
   */
  supportsDarkMode?: boolean;
  /**
   * Native app will provide Light or Dark theme to webView
   * during initialization.
   */
  theme?: ThemeType; // Light, Dark
}

// MAKE SURE TO SYNC WITH CLIENT TEAMS BEFORE ANY CHANGES!
export interface INativeAppApi {
  /** Opens a native dialog and navigates to the given url */
  openDialog: (url: string, size: DialogSize) => void;

  /** Closes a current dialog if any */
  closeDialog: (portalStateChanged: boolean) => void;

  /** Navigates back to the initial view (dashboard) */
  goBack: () => void;

  /** Shows a toast message */
  showToast: (message: string) => void;

  /** Portal calls this API when a webview page is rendered */
  onWebViewRendered: () => void;

  /**
   * Portal calls this API with an exception code when an API call failed in webview
   * List of exception codes:
   * 1150 : Interactive sign in required
   */
  onApiFailure: (exceptionCode: string) => void;

  /** EXPERIMENTAL: Portal calls this API when an upsell purchase is complete. Client should refresh user license */
  onUpsellPurchaseComplete: () => void;

  /** Launch native notification settings */
  openNotificationSettings: () => void;

  /** Webview calls this API to perform a local authentication */
  authenticateUser: () => void;
}

// MAKE SURE TO SYNC WITH CLIENT TEAMS BEFORE ANY CHANGES!
export interface IPortalApi {
  /**
   * Native applications must call this function to set up two-way communications
   * between portal and a native application
   */
  initialize: (hostObject: INativeHostObject) => string;

  /**
   * Refreshes internal state in Portal so that UI can be reflected w/o page reload
   * Native applications should call this function for the following cases:
   * 1. nativeApi.closeDialog was invoked with portalStateChanges: true
   * 2. native dialog was dismissed as a result of a native dialog dismiss event.
   */
  portalStateChanged: () => void;

  /**
   * Refreshes all states in Portal so that UI can be refreshed with
   * latest responses from dependant APIs w/o page reload. Native applications
   * can call this API to refresh a cached view w/o navigating to the url.
   *
   * @deprecated This API is deprecated. Please use navigateByPageUrl over refresh.
   */
  refresh: () => void;

  /**
   * Native application should call this API when native dialog dismiss event occurred.
   * It's expected to call this API instead of closing a dialog when dialog url contains
   * a query parameter (notifyDialogClosingEvent=true). This mechanism allows to prevent
   * a dialog from closing so webview could handle a scenario such as showing a confirmation dialog
   */
  dialogClosing: () => void;

  /** Blur background */
  blurBackground: (enabled: boolean) => void;

  /**
   * Set theme
   * @param theme {ThemeType}
   */
  setTheme?: (theme: ThemeType) => void;

  /** Sets a portal locale */
  setLocale: (locale: string) => void;

  /**
   * Set font size scale factor
   * ex) '1' means the default size. '0.8' means 80% of default size and '1.5' means 150% of default size.
   */
  setFontScale: (scaleFactor: string) => void;

  /** Navigates to a dialog url via client-side routing in a dialog */
  navigateByDialogUrl: (dialogUrl: string) => void;

  /** Navigates to a page url via client-side routing in a full page */
  navigateByPageUrl: (pageUrl: string) => void;

  /** Native application should call this API upon completion of an authentication check */
  onAuthComplete: (authMechanismName: string, result: AuthResult) => void;
}

declare global {
  interface Window {
    /** C# passing boolean with capital */
    True: boolean;
    False: boolean;

    /** Used by Native client to access functionality exposed by portal web view */
    portalApi: IPortalApi;

    /** Native application APIs that web view consumes */
    nativeAppApi: INativeAppApi;

    /** Native application state set by portalApi.initialize */
    nativeAppState: INativeAppState;

    /** Raw native host object */
    nativeRawHostObject: object;
  }
}

export default function initializeWebView(): void {
  if (pageDataService.isProdEnvironment()) {
    /** Disable mouse right click */
    document.addEventListener('contextmenu', (event) => event.preventDefault());
    /**
     * Disable devTools & view source shortcuts
     * @param e
     */
    document.onkeydown = (e) => {
      if (e.key === 'F12') {
        return false;
      }
      if (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
        return false;
      }
      if (e.ctrlKey && e.shiftKey && (e.key === 'J' || e.key === 'j')) {
        return false;
      }
      if (e.ctrlKey && (e.key === 'U' || e.key === 'u')) {
        return false;
      }
      return true;
    };
  }
  window.True = true;
  window.False = false;
  window.portalApi = {
    setTheme: (theme: ThemeType) => {
      TelemetryProvider.Instance.trackEvent('wvSetTheme', { theme });
      store.dispatch(setWebViewTheme(theme));
    },
    onAuthComplete: (authMechanismName: string, result: AuthResult) => {
      TelemetryProvider.Instance.trackEvent('onAuthComplete', { authMechanismName, result });
      authlog(`onAuthComplete => ${authMechanismName}, ${result}`);
      store.dispatch(setAuthResult(result));
    },
    navigateByPageUrl: (pageUrl: string) => {
      TelemetryProvider.Instance.trackEvent('navigateByPageUrl', { pageUrl });
      store.dispatch(navigateWebViewPageUrl(pageUrl));
    },
    navigateByDialogUrl: (dialogUrl: string) => {
      TelemetryProvider.Instance.trackEvent('navigateByDialogUrl', { dialogUrl });
      store.dispatch(navigateWebViewDialogUrl(dialogUrl));
    },
    setFontScale: (scaleFactor: string) => {
      TelemetryProvider.Instance.trackEvent('wvSetFontScale', { scaleFactor });
      setCustomFontSize(scaleFactor);
    },
    blurBackground: (enabled: boolean) => {
      TelemetryProvider.Instance.trackEvent('wvBlurBackground', { value: enabled.toString() });
      store.dispatch(blurBackground(enabled));
    },
    setLocale: (locale: string) => {
      TelemetryProvider.Instance.trackEvent('wvSetLocale', { locale: locale.toString() });
      store.dispatch(setWebViewLocale(locale));
    },
    portalStateChanged: () => {
      const state = store.getState().webView;
      const { userContext } = store.getState().identityMonitoring;
      store.dispatch(updateWebViewState(state, userContext));
    },
    dialogClosing: () => {
      TelemetryProvider.Instance.trackEvent('wvDialogClosing');
      store.dispatch(setDialogClosing(true));
    },
    refresh: () => {
      TelemetryProvider.Instance.trackEvent('wvRefresh');
      store.dispatch(refreshData());
      store.dispatch(restartPageLoad());
    },
    initialize: (hostObject: INativeHostObject) => {
      try {
        window.nativeRawHostObject = hostObject;
        window.nativeAppApi = hostObject.nativeAppApi;
        window.nativeAppState = hostObject.nativeAppState;
        store.dispatch(initializeNativeAppState(hostObject.nativeAppState));
        if (hostObject.nativeAppState.platform === 'Windows') {
          store.dispatch(setCustomScrollBar(true));
        }
        TelemetryProvider.Instance.trackEvent('wvInitializeSuccess');
        return 'success';
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : err;
        TelemetryProvider.Instance.trackException({
          name: 'wvInitializeException',
          message: errorMessage as string,
        });
        return `failed to initialize portal:  ${errorMessage}`;
      }
    },
  };
}
